import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchEventById, deleteEvent, clearCurrentEvent, EventStatus } from '../redux/slices/eventsSlice';
import { eventsAPI } from '../services/eventsApi';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const STATUS_META: Record<EventStatus, { label: string; color: string; bg: string }> = {
  draft:       { label: 'Draft',       color: '#92400e', bg: '#fef3c7' },
  published:   { label: 'Published',   color: '#065f46', bg: '#d1fae5' },
  upcoming:    { label: 'Upcoming',    color: '#1e40af', bg: '#dbeafe' },
  in_progress: { label: 'Live',        color: '#7c3aed', bg: '#ede9fe' },
  completed:   { label: 'Completed',   color: '#374151', bg: '#f3f4f6' },
  cancelled:   { label: 'Cancelled',   color: '#991b1b', bg: '#fee2e2' },
  postponed:   { label: 'Postponed',   color: '#92400e', bg: '#fff7ed' },
};

const formatDateTime = (d: string) =>
  new Date(d).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const InfoRow: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div style={{
    display: 'flex', gap: 12, padding: '10px 0',
    borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start',
  }}>
    <span style={{ fontSize: 16, minWidth: 24, textAlign: 'center' }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{value}</div>
    </div>
  </div>
);

const EventDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentEvent, loading } = useAppSelector(s => s.events);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) dispatch(fetchEventById(id));
    return () => { dispatch(clearCurrentEvent()); };
  }, [id]);

  const handleDelete = async () => {
    if (!currentEvent) return;
    if (!window.confirm(`Delete "${currentEvent.title}"? This action cannot be undone.`)) return;
    setDeleting(true);
    try {
      await dispatch(deleteEvent(currentEvent.id)).unwrap();
      navigate('/events');
    } catch (err: any) {
      setError(typeof err === 'string' ? err : (err?.message || 'Failed to delete event'));
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    if (!currentEvent) return;
    try {
      const res = await eventsAPI.exportRegistrations(currentEvent.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentEvent.slug}-registrations.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  if (loading || !currentEvent) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spinner animation="border" style={{ color: '#00579a' }} />
      </div>
    );
  }

  const event = currentEvent;
  const meta = STATUS_META[event.status] || STATUS_META.draft;
  const images = event.documents?.filter(d => d.file_type === 'image') || [];
  const docs = event.documents?.filter(d => d.file_type === 'document') || [];

  const featureToggles = [
    { label: 'Registration Required',   on: event.registration_required },
    { label: 'Manual Approval',         on: event.require_manual_approval },
    { label: 'Proof of Payment',        on: event.require_proof_of_payment },
    { label: 'ID Verification',         on: event.require_id_verification },
    { label: 'Facial Verification',     on: event.require_facial_verification },
  ].filter(t => t.on);

  return (
    <div style={{ backgroundColor: '#f8f9fc', minHeight: '100vh', paddingTop: 32, paddingBottom: 60 }}>
      <Container fluid style={{ maxWidth: 1100 }}>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {/* Breadcrumb + actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => navigate('/events')}
              style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#64748b' }}
            >
              ← Events
            </button>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>{event.title}</span>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {event.registration_required && (
              <>
                <button
                  onClick={() => navigate(`/events/${event.id}/registrations`)}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500 }}
                >
                  👥 Registrations ({event.registration_count ?? 0})
                </button>
                <button
                  onClick={handleExport}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500 }}
                >
                  ⬇ Export CSV
                </button>
              </>
            )}
            <button
              onClick={() => navigate(`/events/${event.id}/edit`)}
              style={{ background: '#00579a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Edit Event
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{ background: '#fff0f0', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}
            >
              {deleting ? '...' : 'Delete'}
            </button>
          </div>
        </div>

        <Row>
          {/* Main */}
          <Col lg={8}>
            {/* Hero Image */}
            {(event.featured_image || event.poster_image) && (
              <div style={{ marginBottom: 16 }}>
                <img
                  src={`${API_URL}/${event.featured_image || event.poster_image}`}
                  alt={event.title}
                  style={{ width: '100%', borderRadius: 12, objectFit: 'cover', maxHeight: 320 }}
                />
              </div>
            )}

            {/* Title card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>{event.title}</h1>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                    color: meta.color, background: meta.bg,
                  }}>
                    {meta.label}
                  </span>
                  {event.is_virtual && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, color: '#6366f1', background: '#eef2ff' }}>
                      Virtual
                    </span>
                  )}
                  {!event.is_free && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, color: '#065f46', background: '#d1fae5' }}>
                      Paid
                    </span>
                  )}
                </div>
              </div>

              {event.category && (
                <span style={{ fontSize: 12, background: '#f1f5f9', color: '#374151', padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>
                  {event.category}
                </span>
              )}

              {event.summary && (
                <p style={{ fontSize: 15, color: '#374151', marginTop: 16, marginBottom: 0, lineHeight: 1.6 }}>
                  {event.summary}
                </p>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>About This Event</h2>
                <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {event.description}
                </div>
              </div>
            )}

            {/* Gallery images */}
            {images.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Images ({images.length})</h2>
                <Row>
                  {images.map(img => (
                    <Col xs={6} md={4} key={img.id} style={{ marginBottom: 8 }}>
                      <img
                        src={`${API_URL}/${img.file_path}`}
                        alt={img.file_name}
                        style={{ width: '100%', borderRadius: 8, objectFit: 'cover', height: 120, cursor: 'pointer' }}
                        onClick={() => window.open(`${API_URL}/${img.file_path}`, '_blank')}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {/* Documents */}
            {docs.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Attachments ({docs.length})</h2>
                {docs.map(doc => (
                  <a
                    key={doc.id}
                    href={`${API_URL}/${doc.file_path}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', background: '#f8f9fc', borderRadius: 8,
                      textDecoration: 'none', marginBottom: 8, color: '#374151',
                    }}
                  >
                    <span>📄</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{doc.file_name}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>↗</span>
                  </a>
                ))}
              </div>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Event info */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Event Details</h2>

              <InfoRow icon="📅" label="Start" value={formatDateTime(event.start_date)} />
              {event.end_date && <InfoRow icon="🏁" label="End" value={formatDateTime(event.end_date)} />}
              {event.venue_name && <InfoRow icon="🏛" label="Venue" value={event.venue_name} />}
              {event.location && <InfoRow icon="📍" label="Location" value={event.location} />}
              {event.is_virtual && event.virtual_link && (
                <InfoRow icon="💻" label="Join Link" value={
                  <a href={event.virtual_link} target="_blank" rel="noreferrer" style={{ color: '#00579a', wordBreak: 'break-all' }}>
                    {event.virtual_link}
                  </a>
                } />
              )}
              {!event.is_free && event.price && (
                <InfoRow icon="💰" label="Price" value={`${event.currency} ${Number(event.price).toFixed(2)}`} />
              )}
              {event.registration_deadline && (
                <InfoRow icon="⏰" label="Reg. Deadline" value={formatDateTime(event.registration_deadline)} />
              )}
            </div>

            {/* Registration stats */}
            {event.registration_required && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Registrations</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: '#00579a' }}>{event.registration_count ?? 0}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>Registered</div>
                  </div>
                  {event.capacity && (
                    <>
                      <div style={{ height: 40, width: 1, background: '#e2e8f0' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#374151' }}>{event.capacity}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>Capacity</div>
                      </div>
                      <div style={{ height: 40, width: 1, background: '#e2e8f0' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: '#16a34a' }}>
                          {Math.max(0, event.capacity - (event.registration_count ?? 0))}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>Remaining</div>
                      </div>
                    </>
                  )}
                </div>

                {event.capacity && (
                  <div style={{ background: '#f1f5f9', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 8,
                      background: '#00579a',
                      width: `${Math.min(100, ((event.registration_count ?? 0) / event.capacity) * 100)}%`,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                )}

                <button
                  onClick={() => navigate(`/events/${event.id}/registrations`)}
                  style={{
                    marginTop: 12, width: '100%',
                    background: '#f0f7ff', color: '#00579a', border: 'none',
                    borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Manage Registrations →
                </button>
              </div>
            )}

            {/* Feature toggles active */}
            {featureToggles.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Active Features</h2>
                {featureToggles.map(t => (
                  <div key={t.label} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13,
                  }}>
                    <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
                    <span style={{ color: '#374151' }}>{t.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Poster */}
            {event.poster_image && (
              <div style={{ background: '#fff', borderRadius: 12, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Event Poster</h2>
                <img
                  src={`${API_URL}/${event.poster_image}`}
                  alt="Poster"
                  style={{ width: '100%', borderRadius: 8 }}
                />
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EventDetailPage;